import { useSnackbar } from 'notistack';
import { BaseSyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import {
  DefaultValues,
  FieldValues,
  Resolver,
  UseFormReturn,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { ProposalProps } from '~/data/proposals';
import { useProposalUtils } from '~/data/proposals/useProposalUtils';
import { useHasChanged } from '~/hooks/useHasChanged';
import { LegacyProposalRequestProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/data';
import {
  EmployeeProposalFormData,
  EmployeeProposalFormDataWithAttachment,
  EmployeeProposalUpdateCallback,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/types';
import { useResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useResponsibleHRSelect';

type HandleSubmit = (e?: BaseSyntheticEvent) => Promise<void>;

type UseInitiateFormDataProps<
  T extends ProposalProps,
  TFormData extends EmployeeProposalFormData,
> = {
  proposal: T;
  employeeId: number;
  dataMapper: (
    targetEmploymentId: number,
    proposal?: T['proposalData'],
    responsibleHR?: number,
  ) => TFormData;
  resolver: Resolver<TFormData, object>;
};

type useInitiateFormDataOutput<
  TProposalProps extends ProposalProps,
  TFormData extends FieldValues,
> = {
  form: UseFormReturn<TFormData>;
  handleFormReset: () => void;
  handleSubmitRequest: <TSubmission>(props: {
    onSuccess?: EmployeeProposalUpdateCallback;
    handleSubmission: (
      data: TFormData,
      requestProps: LegacyProposalRequestProps,
    ) => Promise<TSubmission>;
  }) => HandleSubmit;
  targetEmploymentId: number;
  allResponsibleHRIds: number[];
  isLoading: boolean;
  currentResponsibleHRId: number;
  shouldResetForm: boolean;
  getInitialFieldValues: (
    proposalData?: TProposalProps['proposalData'],
  ) => TFormData;
  quickFixEnsureAttachmentFileInPayload: <
    TAttachmentFormData extends EmployeeProposalFormDataWithAttachment,
  >(
    data: TAttachmentFormData,
    attachmentFile?: File | null,
  ) => TAttachmentFormData;
};

export const useInitiateFormData = <
  TProposalProps extends ProposalProps,
  TFormData extends EmployeeProposalFormData,
>(
  props: UseInitiateFormDataProps<TProposalProps, TFormData>,
): useInitiateFormDataOutput<TProposalProps, TFormData> => {
  const { proposal, employeeId, dataMapper, resolver } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Target Employment Id
  const { allEmployees } = useAllEmployees();
  const targetEmploymentId = useMemo(
    () => allEmployees?.[employeeId]?.fEmpEmplId || -1,
    [allEmployees, employeeId],
  );

  // Responsible HR options & default
  const { allResponsibleHRIds, defaultResponsibleHrId, isLoading } =
    useResponsibleHRSelect(employeeId);
  const allResponsibleHRIdsChanged = useHasChanged(allResponsibleHRIds);
  const shouldResetForm = useMemo(() => {
    return allResponsibleHRIdsChanged && !isLoading;
  }, [allResponsibleHRIdsChanged, isLoading]);

  // Resolve current responsible HR Id
  const { getResponsibleHRProposalAgent } = useProposalUtils();
  const getCurrentResponsibleHRId = useCallback(
    (proposal?: TProposalProps): number =>
      (!!proposal && getResponsibleHRProposalAgent(proposal)?.userId) ||
      defaultResponsibleHrId ||
      -1,
    [getResponsibleHRProposalAgent, defaultResponsibleHrId],
  );
  const currentResponsibleHRId = useMemo(
    () => getCurrentResponsibleHRId(proposal),
    [getCurrentResponsibleHRId, proposal],
  );

  // Initial field values
  const getInitialFieldValues = useCallback(
    (proposalData?: TProposalProps['proposalData']): TFormData => {
      return dataMapper(
        targetEmploymentId,
        proposalData || proposal?.proposalData,
        currentResponsibleHRId,
      );
    },
    [proposal, dataMapper, currentResponsibleHRId, targetEmploymentId],
  );

  const form = useForm<TFormData>({
    mode: 'all',
    defaultValues: getInitialFieldValues() as DefaultValues<TFormData>,
    resolver: resolver as never,
  });

  const {
    reset,
    formState: { isSubmitting },
    handleSubmit,
  } = form;

  const handleFormReset = useCallback(() => {
    reset(getInitialFieldValues());
  }, [reset, getInitialFieldValues]);

  const requestProps: LegacyProposalRequestProps = useMemo(() => {
    return {
      proposalId: !!proposal.proposalId ? proposal.proposalId : undefined,
      employmentId: targetEmploymentId,
      employeeId,
    };
  }, [proposal.proposalId, targetEmploymentId, employeeId]);

  const handleSubmitRequest = useCallback(
    <TSubmission>(props: {
      onSuccess?: EmployeeProposalUpdateCallback;
      handleSubmission: (
        data: TFormData,
        requestProps: LegacyProposalRequestProps,
      ) => Promise<TSubmission>;
    }): HandleSubmit => {
      const { onSuccess, handleSubmission } = props;

      // Prevent double submissions
      if (isSubmitting) {
        enqueueSnackbar(t('formIsSubmitting'), {
          variant: 'error',
        });
        return () => Promise.resolve();
      }

      return handleSubmit(async (data) => {
        await handleSubmission(data, requestProps)
          .then(() => {
            enqueueSnackbar(t('success'), {
              variant: 'success',
            });
            if (!!onSuccess) {
              onSuccess();
            }
            handleFormReset();
          })
          .catch((err) => {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          });
      });
    },
    [
      requestProps,
      handleFormReset,
      handleSubmit,
      isSubmitting,
      enqueueSnackbar,
      t,
    ],
  );

  // TODO: Investigate issue with handleSubmit not returning File
  //  When File is selected, value for attachment from handleSubmit is always null,
  //  but getValues returns selected File object.
  const quickFixEnsureAttachmentFileInPayload = useCallback(
    <TAttachmentFormData extends EmployeeProposalFormDataWithAttachment>(
      data: TAttachmentFormData,
      attachmentFile?: File | null,
    ): TAttachmentFormData => {
      if (!!attachmentFile && data.hasOwnProperty('attachment')) {
        console.log(attachmentFile, data.attachment);
        return {
          ...data,
          attachment: attachmentFile,
        };
      }

      return data;
    },
    [],
  );

  useEffect(() => {
    if (shouldResetForm) {
      handleFormReset();
    }
  }, [handleFormReset, shouldResetForm]);

  return {
    form,
    handleFormReset,
    handleSubmitRequest,
    targetEmploymentId,
    allResponsibleHRIds,
    isLoading,
    currentResponsibleHRId,
    shouldResetForm,
    getInitialFieldValues,
    quickFixEnsureAttachmentFileInPayload,
  };
};
